import React, { useMemo, useState } from 'react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/combobox';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export type SearchableNavItem = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  /** `data-tour` value, for items a product tour spotlights. */
  tour?: string;
  /** When set, the row stays visible, does not run, and shows this on hover. */
  lockTooltip?: string;
};

type SearchableNavMenuProps = {
  id: string;
  triggerLabel: string;
  items: SearchableNavItem[];
  onSelect: (item: SearchableNavItem) => void;
  selectedId?: string;
  triggerVariant?: React.ComponentProps<typeof Button>['variant'];
  triggerClassName?: string;
  /** Controlled open state, for callers that open the menu themselves. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * Dark searchable menu used by Corporate View More and + Create.
 *
 * Same Combobox pattern as inline action cells: type to filter by label, then
 * run the existing tab/create handler.
 */
export const SearchableNavMenu: React.FC<SearchableNavMenuProps> = ({
  id,
  triggerLabel,
  items,
  onSelect,
  selectedId,
  triggerVariant = 'outline',
  triggerClassName,
  open: openProp,
  onOpenChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) ?? null,
    [items, selectedId]
  );

  return (
    <Combobox
      items={items}
      value={selected}
      open={open}
      onOpenChange={setOpen}
      itemToStringValue={(item) => (item as SearchableNavItem).label}
      onValueChange={(next) => {
        const item = next as SearchableNavItem | null;
        if (!item || item.lockTooltip) return;
        setOpen(false);
        onSelect(item);
      }}
    >
      <ComboboxTrigger
        id={id}
        render={
          <Button
            type="button"
            variant={triggerVariant}
            size="sm"
            className={triggerClassName}
          />
        }
      >
        {triggerLabel}
      </ComboboxTrigger>
      <ComboboxContent className="dark tw:min-w-64">
        <ComboboxInput placeholder="Search…" showTrigger={false} />
        <ComboboxEmpty>No matches</ComboboxEmpty>
        <ComboboxList>
          {(item: SearchableNavItem) =>
            item.lockTooltip ? (
              <TooltipProvider key={item.id}>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span className="tw:block tw:w-full tw:cursor-not-allowed" />
                    }
                  >
                    <ComboboxItem
                      value={item}
                      disabled
                      data-tour={item.tour}
                      className="tw:pointer-events-none tw:w-full tw:whitespace-nowrap tw:opacity-60"
                    >
                      {item.icon}
                      {item.label}
                    </ComboboxItem>
                  </TooltipTrigger>
                  <TooltipContent>{item.lockTooltip}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <ComboboxItem
                key={item.id}
                value={item}
                data-tour={item.tour}
                className="tw:whitespace-nowrap"
              >
                {item.icon}
                {item.label}
              </ComboboxItem>
            )
          }
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default SearchableNavMenu;
